/**
 * Pure helpers for `/inventory/garments` — the finished-garment shelf tab (finished-garment-stock v1
 * PR-2). IO-free, same split as `count-shelf-lib` / `scan-lib`: this file decides, the page renders.
 *
 * The ledger's rows are keyed by the canonical physical identity (placements + exact blank ssSku);
 * the PAGE groups them by the placements half (one group per design set — the way the owner thinks:
 * "the Grim Reaper hoodie, what sizes do we have?"), with one row per blank variant inside, sorted
 * by size.
 */
import type { components } from '@/lib/api/types.gen';
import { sizeRank } from './inventory-lib';

export type GarmentStockRow = components['schemas']['GarmentStockOnHand'];
export type GarmentStockPlacement = GarmentStockRow['placements'][number];

export type GarmentStockGroup = {
  /** The placements half of the stockKey — the group identity. */
  placementsKey: string;
  placements: GarmentStockPlacement[];
  /** Display title: the BACK design leads (it is the distinctive art — ~100 backs vs ~3 fronts),
   *  the rest follow. Falls back to designIds for pre-name rows. */
  title: string;
  rows: GarmentStockRow[];
  totalOnHand: number;
};

/** The placements half of a stockKey (everything before the LAST `#`; the ssSku never contains one). */
export function placementsKeyOf(stockKey: string): string {
  const i = stockKey.lastIndexOf('#');
  return i === -1 ? stockKey : stockKey.slice(0, i);
}

/** Group title: back design first (the identity that distinguishes this shop's garments), then the rest. */
export function groupTitle(placements: readonly GarmentStockPlacement[]): string {
  const named = [...placements].sort((a, b) => {
    if (a.location === 'back') return -1;
    if (b.location === 'back') return 1;
    return a.location.localeCompare(b.location);
  });
  return named.map((p) => p.designName ?? p.designId).join(' · ');
}

/** Group rows by design set; rows inside a group sort by size then colour then sku. */
export function groupGarmentStock(rows: readonly GarmentStockRow[]): GarmentStockGroup[] {
  const byKey = new Map<string, GarmentStockRow[]>();
  for (const r of rows) {
    const k = placementsKeyOf(r.stockKey);
    const g = byKey.get(k);
    if (g) g.push(r);
    else byKey.set(k, [r]);
  }
  const groups = Array.from(byKey.entries()).map(([placementsKey, groupRows]) => {
    const sorted = [...groupRows].sort(
      (a, b) =>
        sizeRank(a.sizeName ?? '') - sizeRank(b.sizeName ?? '') ||
        (a.colorName ?? '').localeCompare(b.colorName ?? '') ||
        a.ssSku.localeCompare(b.ssSku),
    );
    return {
      placementsKey,
      placements: sorted[0]?.placements ?? [],
      title: groupTitle(sorted[0]?.placements ?? []),
      rows: sorted,
      totalOnHand: sorted.reduce((s, r) => s + r.onHand, 0),
    };
  });
  return groups.sort(
    (a, b) => a.title.localeCompare(b.title) || a.placementsKey.localeCompare(b.placementsKey),
  );
}

export type KindFilter = 'all' | 'tshirt' | 'hoodie';

/** Search over the group's design names + style codes (joined from the designs query) + blank names. */
export function matchesStockSearch(
  group: GarmentStockGroup,
  q: string,
  styleCodeByDesignId: ReadonlyMap<string, string>,
): boolean {
  const f = q.trim().toLowerCase();
  if (!f) return true;
  if (group.title.toLowerCase().includes(f)) return true;
  for (const p of group.placements) {
    const code = styleCodeByDesignId.get(p.designId);
    if (code?.toLowerCase().includes(f)) return true;
  }
  return group.rows.some((r) =>
    `${r.brandName ?? ''} ${r.styleName ?? ''} ${r.colorName ?? ''}`.toLowerCase().includes(f),
  );
}

/** Kind filter: a group shows if ANY row matches (a mixed group is rare but must not vanish). */
export function matchesKind(group: GarmentStockGroup, kind: KindFilter): boolean {
  if (kind === 'all') return true;
  return group.rows.some((r) => r.apparelKind === kind);
}

/** One human line for a row's blank: `Gildan 18500 · Black · L`, falling back to the bare sku. */
export function rowBlankLabel(r: GarmentStockRow): string {
  const name = [r.brandName, r.styleName].filter(Boolean).join(' ');
  const variant = [r.colorName, r.sizeName].filter(Boolean).join(' · ');
  if (!name && !variant) return r.ssSku;
  return [name, variant].filter(Boolean).join(' · ');
}
